import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Modal, Nav, Row } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { AlertTriangle, ExternalLink } from 'react-feather';
import { toast } from 'react-toastify';
import { getRenewalCalendarAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { platformUrl } from '../../../utils/mhNav';
import styles from './RenewalCalendar.module.scss';

const localizer = momentLocalizer(moment);

function eventStyle(e) {
  const h = e.resource?.renewalHealth;
  let bg = '#54ba4a';
  if (h === 'at_risk') bg = '#ffaa05';
  if (h === 'overdue') bg = '#fc4438';
  return { style: { backgroundColor: bg, border: 'none' } };
}

function KanbanCard({ ev, onTask, onEmail }) {
  return (
    <Card className="mb-2 small">
      <Card.Body className="py-2">
        <div className="fw-semibold">{ev.companyName}</div>
        <div className="text-muted">{ev.planName}</div>
        <div>{new Date(ev.startsAt).toLocaleDateString()}</div>
        <div>MRR ${Number(ev.mrrUsd || 0).toFixed(0)}</div>
        <div className="text-muted">{ev.ownerName}</div>
        <div className="d-flex flex-wrap gap-1 mt-2">
          <Button
            size="sm"
            variant="link"
            className="p-0"
            href={platformUrl(`tenants/${ev.tenantId}`)}
            target="_blank"
            rel="noreferrer"
          >
            Open Tenant <ExternalLink size={12} />
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => onTask(ev)}>
            Create Task
          </Button>
          <Button size="sm" variant="outline-primary" onClick={() => onEmail(ev)}>
            Send Renewal Note
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function RenewalCalendar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('calendar');
  const [taskOpen, setTaskOpen] = useState(null);
  const [emailOpen, setEmailOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRenewalCalendarAPI();
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const calEvents = useMemo(
    () =>
      (data?.events || []).map((e) => ({
        title: `${e.companyName} (${e.planName})`,
        start: new Date(e.startsAt),
        end: new Date(e.endsAt),
        resource: e,
      })),
    [data]
  );

  const pipeline = useMemo(() => {
    const evs = data?.events || [];
    const now = Date.now();
    const upcoming = [];
    const soon = [];
    const overdue = [];
    evs.forEach((e) => {
      const t = new Date(e.startsAt).getTime();
      const days = (t - now) / 86400000;
      if (t < now || e.renewalHealth === 'overdue') overdue.push(e);
      else if (days < 30) soon.push(e);
      else upcoming.push(e);
    });
    return { upcoming, soon, overdue };
  }, [data]);

  if (loading && !data) {
    return (
      <div>
        <PageHeader
          title="Renewal Calendar"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Subscriptions', path: platformUrl('subscriptions') },
            { label: 'Renewals' },
          ]}
        />
        <LoadingSkeleton rows={5} cols={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Renewal Calendar"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Subscriptions', path: platformUrl('subscriptions') },
            { label: 'Renewals' },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load renewals"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Renewal Calendar"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Subscriptions', path: platformUrl('subscriptions') },
          { label: 'Renewals' },
        ]}
      />

      <Nav variant="pills" className="mb-3">
        <Nav.Item>
          <Nav.Link active={view === 'calendar'} onClick={() => setView('calendar')}>
            Month calendar
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link active={view === 'pipeline'} onClick={() => setView('pipeline')}>
            Pipeline list
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Row>
        <Col lg={9}>
          {view === 'calendar' && (
            <div className={`${styles.calWrap} bg-white p-2 rounded-3 border`}>
              <Calendar
                localizer={localizer}
                events={calEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 560 }}
                views={['month']}
                defaultView="month"
                eventPropGetter={(e) => eventStyle(e)}
                onSelectEvent={(e) => {
                  window.open(platformUrl(`tenants/${e.resource.tenantId}`), '_blank', 'noopener,noreferrer');
                }}
              />
            </div>
          )}
          {view === 'pipeline' && (
            <Row className="g-2">
              <Col md={4}>
                <h6>Upcoming (30+ days)</h6>
                {pipeline.upcoming.map((ev) => (
                  <KanbanCard key={ev.id} ev={ev} onTask={setTaskOpen} onEmail={setEmailOpen} />
                ))}
              </Col>
              <Col md={4}>
                <h6>Soon (&lt; 30 days)</h6>
                {pipeline.soon.map((ev) => (
                  <KanbanCard key={ev.id} ev={ev} onTask={setTaskOpen} onEmail={setEmailOpen} />
                ))}
              </Col>
              <Col md={4}>
                <h6>Overdue</h6>
                {pipeline.overdue.map((ev) => (
                  <KanbanCard key={ev.id} ev={ev} onTask={setTaskOpen} onEmail={setEmailOpen} />
                ))}
              </Col>
            </Row>
          )}
        </Col>
        <Col lg={3}>
          <Card>
            <Card.Header>At-Risk Renewals</Card.Header>
            <Card.Body className="small">
              {(data?.atRiskSidebar || []).map((x) => (
                <div key={x.id} className="mb-3 pb-2 border-bottom">
                  <div className="fw-semibold">{x.companyName}</div>
                  <div>Usage {x.usagePctLast30d}% (30d)</div>
                  <div className="text-muted">Owner: {x.ownerName}</div>
                  <div>{x.recommendedAction}</div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={Boolean(taskOpen)} onHide={() => setTaskOpen(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control as="textarea" rows={3} placeholder="Task description…" />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              toast.success('Task created (simulated)');
              setTaskOpen(null);
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(emailOpen)} onHide={() => setEmailOpen(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Renewal note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control as="textarea" rows={6} defaultValue={`Hi ${emailOpen?.ownerName},\n\nFollowing up on renewal for ${emailOpen?.companyName}…`} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              toast.success('Email queued (simulated)');
              setEmailOpen(null);
            }}
          >
            Send
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
